import type { WellAllocationRequest } from "@ots-frontend/ots-data";
import { useState } from "react";
import { type ContainerLevelRequest, ConnectedContainerLevelDialog } from "../../tank-chart/connected/ConnectedContainerLevelDialog";
import type { OtsBrand, OtsColorSchemePreference } from "../../theme/OtsTheme";
import { ConnectedWellAllocationDialog } from "../../well-allocation/connected/ConnectedWellAllocationDialog";
import { createActionProvider, type DriverActivityActionProvider } from "../driver-activity-actions";
import { DRIVER_ACTIVITY_PERMISSIONS } from "../driver-activity-permissions";
import { tankLevelRequest, wellAllocationRequest } from "../model/activity-quantity";

export type UseDriverActivityQuantityToolsOptions = {
  timeZone?: string;
  volumeUnit?: string;
  brand?: OtsBrand;
  colorScheme?: OtsColorSchemePreference;
  /** Whether this person holds a permission; everything is offered when omitted. */
  can?: (permission: string) => boolean;
};

/**
 * A ticket's tank levels and well allocation, each in its own dialog, as WaterGo's Quantity tools open them. Tank
 * levels are for anyone; well allocation, as in WaterGo, for those granted it.
 */
export const useDriverActivityQuantityTools = ({ timeZone, volumeUnit, brand, colorScheme, can = () => true }: UseDriverActivityQuantityToolsOptions = {}): DriverActivityActionProvider => {
  const [tank, setTank] = useState<ContainerLevelRequest>();
  const [allocation, setAllocation] = useState<WellAllocationRequest>();
  return createActionProvider(
    [
      { id: "tank-level", label: "Tank Level", scope: "single", group: "tools", readOnly: true, isVisible: ([entry]) => !!entry && !!tankLevelRequest(entry) },
      ...(can(DRIVER_ACTIVITY_PERMISSIONS.wellAllocation)
        ? [{ id: "well-allocation", label: "Well Allocation", scope: "single", group: "tools", readOnly: true, isVisible: ([entry]) => !!wellAllocationRequest(entry?.tta) }] as const
        : []),
    ],
    {
      "tank-level": ([entry]) => entry && setTank(tankLevelRequest(entry)),
      "well-allocation": ([entry]) => setAllocation(wellAllocationRequest(entry?.tta)),
    },
    <>
      <ConnectedContainerLevelDialog request={tank} onClose={() => setTank(undefined)} timeZone={timeZone} volumeUnit={volumeUnit} brand={brand} colorScheme={colorScheme}/>
      <ConnectedWellAllocationDialog request={allocation} onClose={() => setAllocation(undefined)} volumeUnit={volumeUnit} brand={brand} colorScheme={colorScheme}/>
    </>,
  );
};
